import React from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { useProfile } from "../../../contexts/ProfileContext";
import {
  fetchManualMembers,
  fetchTeamMembers,
  updateTeamMemberRole,
} from "../../../store/slices/memberSlice";
import CardTable from "../../../components/common/CardTable";
import { TableRow } from "../../../components/common/Table/Table";
import MembersTable from "./MembersTable";
import { DropdownOption } from "../../../components/common/Dropdown";
import { defaultedMembersColumns } from "./membersColumns";
import { mapRowsWithDropdown } from "./membersHelpers";
import { SimpleTableTabProps } from "./membersTypes";

/**
 * Renders manually added members with role management and pagination.
 * Refetches team members and contacts after role changes to ensure email lists update in real-time.
 * @param props - Manual members table state and handlers.
 * @returns The manually added members tab content.
 */
const ManuallyAddedMembersTab: React.FC<SimpleTableTabProps> = (props) => {
  const { manuallyAddedMembers, manualTotalItems } = props;
  const actualRows: TableRow[] = manuallyAddedMembers ?? [];
  const currentPage = props.manualPage ?? 1;
  const onPageChange = props.setManualPage ?? (() => {});
  const ipp = props.itemsPerPage ?? 10;

  const { profile } = useProfile();
  const isAdmin = profile?.isAdmin;
  const loginNuid = profile?.nuid ? String(profile.nuid) : null;
  const dispatch = useAppDispatch();
  const memberAccess = useAppSelector((state) => state.membersSlice.memberAccess);
  const normalizedMemberAccess =
    (memberAccess as any)?.data ??
    (memberAccess as any)?.access ??
    memberAccess;
  const canMemberShipEdit = Boolean(
    normalizedMemberAccess?.canMemberShipEdit ??
      normalizedMemberAccess?.canMembershipEdit ??
      false,
  );

  /**
   * Handles member role changes and refreshes both member list and contacts for real-time email updates.
   * @param row - The member row being updated.
   * @param selected - The newly selected role option.
   */
  const handleRoleChange = async (row: any, selected: DropdownOption) => {
    if (!props.teamId) return;

    const data = row.originalData ?? row;
    const updateResult: {
      meta?: { requestStatus?: string };
      type?: string;
    } = await dispatch(
      updateTeamMemberRole({
        memberType: "manual",
        nuid: data.nuid,
        teamId: props.teamId,
        newStatus: selected.value,
        currentStatus: data.teamRole,
        startDate: data.startDate ?? data.startdate ?? "",
        deptPk: data.deptPk,
        privStartDate: data.privStartDate ?? data.privlegestartdate ?? "",
      }),
    );

    const isUpdateSuccessful =
      updateResult?.meta?.requestStatus === "fulfilled" ||
      updateResult?.type === "members/updateTeamMemberRole/fulfilled" ||
      updateResult?.type?.endsWith("/fulfilled") === true;

    if (!isUpdateSuccessful) {
      return;
    }

    const apiSortColumn =
      sortColumn === "lastName" ? "LAST_NAME" : "FIRST_NAME";
    const apiSortOrder = sortDirection.toUpperCase() as "ASC" | "DESC";

    // If the current page had more than one record the page is still populated
    // after this removal — re-fetch the same page (page state won't change so
    // the parent useEffect won't fire; we dispatch directly).
    // If it had exactly one record the page is now empty — move back one page
    // and let the parent's useEffect fire fetchManualMembers for that page.
    if (actualRows.length > 1) {
      dispatch(
        fetchManualMembers({
          teamId: props.teamId,
          sortColumn: apiSortColumn,
          sortOrder: apiSortOrder,
          page: currentPage,
          per_page: ipp,
        }),
      );
    } else if (currentPage > 1) {
      // Last member on this page removed; parent useEffect will fetch page - 1.
      onPageChange(currentPage - 1);
    } else {
      // Last member on page 1 removed; dispatch directly (page state unchanged).
      dispatch(
        fetchManualMembers({
          teamId: props.teamId,
          sortColumn: apiSortColumn,
          sortOrder: apiSortOrder,
          page: 1,
          per_page: ipp,
        }),
      );
    }

    // Refetch team members to update contacts and sponsor email lists in real-time
    dispatch(fetchTeamMembers({ teamId: props.teamId }));

    // Notify parent to refresh the UBT Membership Summary counts
    props.onRoleUpdated?.();
  };

  const sortColumn = props.sortColumn ?? "lastName";
  const sortDirection = props.sortDirection ?? "asc";
  const setSortColumn = props.setSortColumn ?? (() => {});
  const setSortDirection = props.setSortDirection ?? (() => {});

  const handleSort = (columnKey: string, direction: "ASC" | "DESC") => {
    const lowerDirection = direction.toLowerCase() as "asc" | "desc";
    setSortColumn(columnKey);
    setSortDirection(lowerDirection);
  };

  const pagedRows = actualRows ?? [];
  const memberRoleOptionsManual = useAppSelector(
    (state) => state.membersSlice.memberRoleOptionsManual ?? [],
  );
  const memberRoleOptionsLoading = useAppSelector(
    (state) => state.membersSlice.memberRoleOptionsLoading,
  );
  const updateMemberRoleLoading = useAppSelector(
    (state) => state.membersSlice.updateMemberRoleLoading,
  );
  const displayRows = mapRowsWithDropdown(
    pagedRows,
    isAdmin,
    "manual-row",
    memberRoleOptionsManual,
    handleRoleChange,
    {
      canMemberShipEdit,
      loginNuid,
      requireAdminForDropdown: true,
    },
  );

  const manualMembersLoading = useAppSelector(
    (state) => state.membersSlice.manualMembersLoading,
  );
  const manualMembersError = useAppSelector(
    (state) => state.membersSlice.manualMembersError,
  );
  const showSingleBanner = props.showSingleBanner;

  return (
    <CardTable className="tab-row-bg">
      <MembersTable
        displayRows={displayRows}
        totalItems={manualTotalItems}
        itemsPerPage={ipp}
        currentPage={currentPage}
        onPageChange={onPageChange}
        columns={defaultedMembersColumns}
        onSort={handleSort}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        loading={manualMembersLoading}
        optionsLoading={memberRoleOptionsLoading}
        error={manualMembersError}
        showSingleBanner={showSingleBanner}
        updateRoleLoading={updateMemberRoleLoading}
        paginationTestId="pagination-manual"
        paginationWrapperTestId="pagination-manual-wrapper"
      />
    </CardTable>
  );
};

export default ManuallyAddedMembersTab;
